import React, { useState } from 'react';
import { HelpCircle, X, ClipboardList, RefreshCw, Wifi, WifiOff, Eye, Download, Lightbulb } from 'lucide-react';

const HELP_SECTIONS = [
  {
    icon: ClipboardList,
    title: 'Counting Products',
    content: 'Tap the quantity field to type a count, or use the +/- buttons. Counts save automatically as you type. Press Enter to jump to the next uncounted item. The progress badge at the bottom shows how many items you\'ve counted.',
  },
  {
    icon: RefreshCw,
    title: 'Syncing Counts',
    content: 'When you\'re ready, go to the Sync tab and tap "Sync Now" to upload your counts to the server. If some items fail, you can retry just the failed ones.',
  },
  {
    icon: WifiOff,
    title: 'Working Offline',
    content: 'All your counts are saved locally on this device. You can count even without internet. Just sync when you\'re back online. An amber banner will show when you\'re offline.',
  },
  {
    icon: Eye,
    title: 'Reviewing Counts',
    content: 'The Review tab shows your counted quantities against expected stock levels. Items are colour-coded: green for match, amber for under, red for over, grey for uncounted.',
  },
  {
    icon: Download,
    title: 'Installing the App',
    content: 'Android / Chrome: Tap the install banner at the top of the screen, or use the browser menu and select "Install app" or "Add to Home Screen".\n\niPad / Safari: Tap the Share button (square with arrow), scroll down, then tap "Add to Home Screen". This gives you a full-screen app experience without the browser toolbar.',
  },
  {
    icon: Lightbulb,
    title: 'Field Tips',
    content: 'Count full cases separately from loose bottles. Work shelf by shelf, left to right. Use the Notes field (chevron icon) to flag anything unusual. Attach photos for damage or expiry evidence — you can add up to 3 photos per observation.',
  },
];

export default function CountHelpDrawer({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" data-testid="count-help-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      {/* Drawer */}
      <div className="relative bg-white rounded-t-2xl w-full max-w-lg max-h-[70vh] overflow-y-auto shadow-xl animate-in slide-in-from-bottom duration-300">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-cyan-600" />
            <h2 className="text-base font-semibold text-slate-900">How to Use AbbeyZone Count</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="help-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-5">
          {HELP_SECTIONS.map((section, i) => {
            const Icon = section.icon;
            return (
              <div key={i} className="flex gap-3" data-testid={`help-section-${i}`}>
                <div className="shrink-0 mt-0.5 p-2 bg-cyan-50 rounded-lg">
                  <Icon className="w-4 h-4 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">{section.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{section.content}</p>
                </div>
              </div>
            );
          })}
          <div className="pt-2 text-center">
            <p className="text-xs text-slate-400">Need more help? Contact your site administrator.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-2 rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors"
      title="Help"
      data-testid="count-help-btn"
    >
      <HelpCircle className="w-5 h-5" />
    </button>
  );
}
